/**
 * Task-event store (framework-free, unit-testable).
 *
 * Holds per-task progress driven by task_* / still_processing / error and
 * chat_stream_* WebSocket events, plus stall detection. Consumers subscribe
 * to one task id (`listen`) and read an immutable snapshot (`getTaskProgress`)
 * — the pair `useSyncExternalStore` needs — so a message for task B never
 * re-renders a component that only watches task A.
 */

import { taskErrorProgress } from '../taskErrorProgress';
import { logDebug, warnDebug, type TaskProgress, type WebSocketMessage } from './types';

export const STALL_TIMEOUT_MS = 45_000; // 45 seconds without any message → may be stalled
export const STALL_CHECK_INTERVAL_MS = 5_000; // Check stall status every 5 seconds
/** How long a catch-up stream waits for its snapshot before replaying buffered deltas. */
export const STREAM_SNAPSHOT_WAIT_MS = 2000;

/** Message types this store consumes. */
export const TASK_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  'task_progress',
  'still_processing',
  'task_complete',
  'task_error',
  'error',
  'chat_stream_start',
  'chat_stream_delta',
  'chat_stream_snapshot',
  'chat_stream_end',
  'chat_stream_error',
  'chat_stream_reset',
]);

interface StreamAcc {
  text: string;
  seq: number;
  pendingSnapshot: boolean;
  deltaBuffer: { delta: string; seq: number }[];
  snapshotTimer: ReturnType<typeof setTimeout> | null;
}

type Listener = () => void;

function blankEntry(taskId: string, stage = ''): TaskProgress {
  return { taskId, stage, progress: 0, message: '', isComplete: false };
}

export class TaskEventStore {
  private progress = new Map<string, TaskProgress>();
  private subscribed = new Set<string>();
  // Per-task last-activity timestamp for stall detection
  private lastActivity = new Map<string, number>();
  private streamAcc = new Map<string, StreamAcc>();
  // One callback per task per kind (a later registration replaces the earlier one).
  private streamDeltaCallbacks = new Map<string, (text: string) => void>();
  private streamErrorCallbacks = new Map<string, (error: string) => void>();
  private streamResetCallbacks = new Map<string, () => void>();
  private listeners = new Map<string, Set<Listener>>();

  // ---------------------------------------------------------------------------
  // Read / subscribe (useSyncExternalStore contract)
  // ---------------------------------------------------------------------------

  getTaskProgress = (taskId: string): TaskProgress | null => {
    return this.progress.get(taskId) || null;
  };

  /** Registers a change listener for one task id; returns the unsubscribe fn. */
  listen = (taskId: string, listener: Listener): (() => void) => {
    let set = this.listeners.get(taskId);
    if (!set) {
      set = new Set();
      this.listeners.set(taskId, set);
    }
    set.add(listener);
    return () => {
      const current = this.listeners.get(taskId);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) this.listeners.delete(taskId);
    };
  };

  subscribedTaskIds(): Iterable<string> {
    return this.subscribed;
  }

  private emit(taskId: string): void {
    const set = this.listeners.get(taskId);
    if (!set) return;
    Array.from(set).forEach((listener) => listener());
  }

  private setEntry(taskId: string, entry: TaskProgress): void {
    this.progress.set(taskId, entry);
    this.emit(taskId);
  }

  private touch(taskId: string, now = Date.now()): void {
    this.lastActivity.set(taskId, now);
  }

  // ---------------------------------------------------------------------------
  // Task lifecycle (driven by consumers)
  // ---------------------------------------------------------------------------

  /** Start tracking a task: mark subscribed, start its stall clock, seed an entry. */
  track(taskId: string): void {
    this.subscribed.add(taskId);
    this.touch(taskId);
    logDebug('[WebSocket] Subscribe task:', taskId);
    if (!this.progress.has(taskId)) {
      this.setEntry(taskId, blankEntry(taskId));
    }
  }

  /** Stop tracking a task. Keeps its last progress entry (readable after unsubscribe). */
  untrack(taskId: string): void {
    this.subscribed.delete(taskId);
    this.lastActivity.delete(taskId);
    logDebug('[WebSocket] Unsubscribe task:', taskId);
  }

  /** Drop a task's progress entry entirely (cleanup after completion). */
  clear(taskId: string): void {
    this.subscribed.delete(taskId);
    this.lastActivity.delete(taskId);
    const had = this.progress.delete(taskId);
    if (had) this.emit(taskId);
  }

  /** Drop all per-user state (logout / user switch, FE-20). */
  reset(): void {
    this.subscribed.clear();
    this.lastActivity.clear();
    this.streamAcc.forEach((acc) => {
      if (acc.snapshotTimer) clearTimeout(acc.snapshotTimer);
    });
    this.streamAcc.clear();
    const affected = Array.from(this.progress.keys());
    this.progress = new Map();
    affected.forEach((taskId) => this.emit(taskId));
  }

  /**
   * Flags in-progress tasks with no progress or still_processing message for
   * STALL_TIMEOUT_MS as `mayBeStalled`.
   */
  checkStalls(now = Date.now()): void {
    const stalledIds: string[] = [];
    this.progress.forEach((tp, taskId) => {
      if (tp.isComplete) return;
      const last = this.lastActivity.get(taskId);
      if (!last) return; // No activity recorded yet
      const elapsed = now - last;
      if (elapsed >= STALL_TIMEOUT_MS && !tp.mayBeStalled) {
        stalledIds.push(taskId);
        warnDebug(
          `[WebSocket] Task ${taskId} may be stalled (no message for ${Math.round(elapsed / 1000)}s)`
        );
      }
    });
    stalledIds.forEach((taskId) => {
      const tp = this.progress.get(taskId)!;
      this.setEntry(taskId, { ...tp, mayBeStalled: true });
    });
  }

  // ---------------------------------------------------------------------------
  // Stream callbacks
  // ---------------------------------------------------------------------------

  onStreamDelta = (taskId: string, cb: (text: string) => void): (() => void) => {
    this.streamDeltaCallbacks.set(taskId, cb);
    return () => { this.streamDeltaCallbacks.delete(taskId); };
  };

  onStreamError = (taskId: string, cb: (error: string) => void): (() => void) => {
    this.streamErrorCallbacks.set(taskId, cb);
    return () => { this.streamErrorCallbacks.delete(taskId); };
  };

  onStreamReset = (taskId: string, cb: () => void): (() => void) => {
    this.streamResetCallbacks.set(taskId, cb);
    return () => { this.streamResetCallbacks.delete(taskId); };
  };

  // Flush a streaming accumulator to the progress entry + fire the delta callback
  private flushAcc(taskId: string, acc: { text: string; seq: number }): void {
    const existing = this.progress.get(taskId);
    this.setEntry(taskId, {
      ...(existing || blankEntry(taskId, 'streaming')),
      streamingText: acc.text,
      streamSeq: acc.seq,
      isStreaming: true,
      mayBeStalled: false,
    });
    this.streamDeltaCallbacks.get(taskId)?.(acc.text);
  }

  // ---------------------------------------------------------------------------
  // Message handling
  // ---------------------------------------------------------------------------

  /** Applies a task-scoped server message. Returns false for types it does not own. */
  handleMessage(message: WebSocketMessage): boolean {
    if (!TASK_MESSAGE_TYPES.has(message.type)) return false;
    const tid = message.task_id;

    switch (message.type) {
      case 'task_progress': {
        if (!tid || !message.payload) break;
        this.touch(tid);
        const prev = this.progress.get(tid);
        this.setEntry(tid, {
          taskId: tid,
          stage: message.payload.stage || '',
          progress: message.payload.progress || 0,
          message: message.payload.message || '',
          metadata: message.payload.metadata,
          isComplete: false,
          mayBeStalled: false,
          // Preserve streaming fields if active
          ...(prev?.streamingText != null ? {
            streamingText: prev.streamingText,
            isStreaming: prev.isStreaming,
            streamSeq: prev.streamSeq,
          } : {}),
        });
        break;
      }

      case 'still_processing': {
        // Heartbeat from a long-running job — reset inactivity timer, clear stall flag
        if (!tid) break;
        this.touch(tid);
        const existing = this.progress.get(tid);
        if (existing) {
          this.setEntry(tid, {
            ...existing,
            stage: message.payload?.stage || existing.stage,
            mayBeStalled: false,
          });
        }
        break;
      }

      case 'task_complete': {
        if (!tid) break;
        this.lastActivity.delete(tid);
        const existing = this.progress.get(tid);
        const result = message.payload?.result;
        const resultStatus = String(result?.status || '').toUpperCase();
        const resultFailed = resultStatus === 'FAILED' || resultStatus === 'FAILURE';
        const resultError = resultFailed
          ? String(result?.error || message.payload?.message || 'Task failed')
          : undefined;
        this.setEntry(tid, {
          taskId: tid,
          stage: resultFailed ? 'error' : 'complete',
          progress: 100,
          message: resultError || message.payload?.message || 'Complete',
          metadata: existing?.metadata,
          isComplete: true,
          result,
          ...(resultError ? { error: resultError } : {}),
          ...(resultFailed && result?.error_code ? { errorCode: String(result.error_code) } : {}),
          ...(resultFailed && result?.error_message_key ? { errorMessageKey: String(result.error_message_key) } : {}),
          // Preserve final streaming text for consumers that read it after completion
          ...(existing?.streamingText != null ? {
            streamingText: existing.streamingText,
            isStreaming: false,
          } : {}),
        });
        break;
      }

      case 'task_error': {
        if (!tid) break;
        this.lastActivity.delete(tid);
        // Carries error_code / error_message_key so consumers localize.
        this.setEntry(tid, taskErrorProgress(tid, message.payload, this.progress.get(tid)));
        break;
      }

      case 'error': {
        if (tid) {
          this.lastActivity.delete(tid);
          const existing = this.progress.get(tid);
          this.setEntry(tid, {
            taskId: tid,
            stage: 'error',
            progress: existing?.progress || 0,
            message: message.message || 'Task failed',
            metadata: existing?.metadata,
            isComplete: true,
            error: message.message || 'Task failed',
          });
        }
        warnDebug('[WebSocket] Server error:', message.message);
        break;
      }

      case 'chat_stream_start': {
        if (!tid) break;
        this.touch(tid);
        const isCatchup = message.payload?.catchup === true;

        if (!this.streamAcc.has(tid)) {
          this.streamAcc.set(tid, {
            text: '', seq: -1,
            pendingSnapshot: isCatchup,
            deltaBuffer: [],
            snapshotTimer: isCatchup
              ? setTimeout(() => {
                  const acc = this.streamAcc.get(tid);
                  if (acc?.pendingSnapshot) {
                    acc.pendingSnapshot = false;
                    for (const d of acc.deltaBuffer) {
                      if (d.seq > acc.seq) {
                        acc.text += d.delta;
                        acc.seq = d.seq;
                      }
                    }
                    acc.deltaBuffer = [];
                    this.flushAcc(tid, acc);
                  }
                }, STREAM_SNAPSHOT_WAIT_MS)
              : null,
          });
        }

        const existing = this.progress.get(tid);
        this.setEntry(tid, {
          ...(existing || blankEntry(tid)),
          isStreaming: true, streamingText: '', streamSeq: -1, stage: 'streaming',
        });
        break;
      }

      case 'chat_stream_delta': {
        if (!tid || message.payload?.delta == null) break;
        this.touch(tid);

        const acc = this.streamAcc.get(tid);
        if (!acc) break;

        const deltaSeq = message.payload.seq ?? acc.seq + 1;

        if (acc.pendingSnapshot) {
          acc.deltaBuffer.push({ delta: message.payload.delta, seq: deltaSeq });
          break;
        }

        if (deltaSeq <= acc.seq) break;
        acc.text += message.payload.delta;
        acc.seq = deltaSeq;

        this.flushAcc(tid, acc);
        break;
      }

      case 'chat_stream_snapshot': {
        if (!tid || message.payload?.text == null) break;
        this.touch(tid);

        const acc = this.streamAcc.get(tid);
        if (!acc) break;

        if (acc.snapshotTimer) {
          clearTimeout(acc.snapshotTimer);
          acc.snapshotTimer = null;
        }

        acc.text = message.payload.text as string;
        acc.seq = (message.payload.seq as number) ?? 0;
        acc.pendingSnapshot = false;

        for (const d of acc.deltaBuffer) {
          if (d.seq > acc.seq) {
            acc.text += d.delta;
            acc.seq = d.seq;
          }
        }
        acc.deltaBuffer = [];

        this.flushAcc(tid, acc);
        break;
      }

      case 'chat_stream_end': {
        if (!tid) break;
        this.touch(tid);
        const acc = this.streamAcc.get(tid);
        if (acc?.snapshotTimer) clearTimeout(acc.snapshotTimer);
        this.streamAcc.delete(tid);
        this.streamDeltaCallbacks.delete(tid);
        this.streamErrorCallbacks.delete(tid);

        const existing = this.progress.get(tid);
        this.setEntry(tid, {
          ...(existing || blankEntry(tid)),
          isStreaming: false, streamMessageId: message.payload?.message_id,
        });
        break;
      }

      case 'chat_stream_error': {
        if (!tid) break;
        this.touch(tid);
        const errorMsg = message.payload?.error || 'Stream interrupted';
        const acc = this.streamAcc.get(tid);
        if (acc?.snapshotTimer) clearTimeout(acc.snapshotTimer);
        this.streamAcc.delete(tid);
        this.streamDeltaCallbacks.delete(tid);

        const existing = this.progress.get(tid);
        this.setEntry(tid, {
          ...(existing || blankEntry(tid)),
          isStreaming: false, streamError: errorMsg,
        });

        this.streamErrorCallbacks.get(tid)?.(errorMsg);
        this.streamErrorCallbacks.delete(tid);
        break;
      }

      case 'chat_stream_reset': {
        if (!tid) break;
        this.touch(tid);
        // Clear accumulator but keep callbacks alive for the retry
        const acc = this.streamAcc.get(tid);
        if (acc?.snapshotTimer) clearTimeout(acc.snapshotTimer);
        this.streamAcc.delete(tid);
        // Not streaming, clear text, NO streamError
        const existing = this.progress.get(tid);
        this.setEntry(tid, {
          ...(existing || blankEntry(tid)),
          isStreaming: false, streamingText: '', streamSeq: -1,
        });
        this.streamResetCallbacks.get(tid)?.();
        break;
      }
    }
    return true;
  }
}
